import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon } from '../../components/UI';
import { ClientDTO, clientService } from '../../services/clientApi';
import SuggestionApiService, {
  PreferredTimeOfDay,
  SuggestedSlot,
  SuggestionResultPayload,
} from '../../services/suggestionApi';
import { getCoordinates } from '../../utils/mapUtils';
import { getUserPreferences, subscribeUserPreferences } from '../../services/userPreferences';

const toInputDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toFrenchDateTime = (value: string): string => {
  const date = new Date(value);
  return date.toLocaleString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toHourMinute = (value: string): string => {
  const date = new Date(value);
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toIsoDateTime = (date: string, time: string): string => {
  return `${date}T${time}:00`;
};

const getDefaultPreferredDuration = (): number => {
  const preferredDuration = getUserPreferences().defaultVisitDuration;
  return Number.isFinite(preferredDuration) && preferredDuration > 0
    ? Math.round(preferredDuration)
    : 60;
};

const getPreferredTimeLabel = (value: PreferredTimeOfDay | ''): string => {
  if (value === 'morning') {
    return 'Matin';
  }
  if (value === 'afternoon') {
    return 'Apres-midi';
  }
  if (value === 'evening') {
    return 'Soiree';
  }
  return 'Sans preference';
};

const SlotCard: React.FC<{
  slot: SuggestedSlot;
  index: number;
  variant: 'primary' | 'alternative';
  onSchedule: () => void;
}> = ({ slot, index, variant, onSchedule }) => {
  const badgeClassName =
    variant === 'primary'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : 'bg-blue-100 text-blue-800 border-blue-200';

  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-gray-500">
            {variant === 'primary' ? `Suggestion #${index + 1}` : `Alternative #${index + 1}`}
          </p>
          <h4 className="text-base font-semibold text-gray-900">{toFrenchDateTime(slot.startTime)}</h4>
          <p className="text-sm text-gray-600">
            {toHourMinute(slot.startTime)} - {toHourMinute(slot.endTime)}
          </p>
        </div>
        <div className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${badgeClassName}`}>
          Score {slot.optimizationScore ?? 0}/100
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg bg-gray-50 px-3 py-2">
          <p className="text-xs uppercase text-gray-500">Trajet avant</p>
          <p className="text-sm font-medium text-gray-900">{slot.travelTimeFromPrevious ?? 0} min</p>
        </div>
        <div className="rounded-lg bg-gray-50 px-3 py-2">
          <p className="text-xs uppercase text-gray-500">Trajet apres</p>
          <p className="text-sm font-medium text-gray-900">{slot.travelTimeToNext ?? 0} min</p>
        </div>
      </div>

      {slot.notes && <p className="text-sm text-gray-600">{slot.notes}</p>}

      <div className="pt-2">
        <Button variant="primary" size="sm" onClick={onSchedule}>
          Proposer ce creneau
        </Button>
      </div>
    </div>
  );
};

export const Suggestions: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [selectedClientId, setSelectedClientId] = useState('');
  const [preferredDuration, setPreferredDuration] = useState(() => getDefaultPreferredDuration());
  const [earliestDate, setEarliestDate] = useState(() => toInputDate(new Date()));
  const [latestDate, setLatestDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return toInputDate(date);
  });
  const [preferredTimeOfDay, setPreferredTimeOfDay] = useState<PreferredTimeOfDay | ''>('');
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [includeBuffer, setIncludeBuffer] = useState(true);
  const [maxIdleTimeMinutes, setMaxIdleTimeMinutes] = useState<number | undefined>(60);
  const [result, setResult] = useState<SuggestionResultPayload | null>(null);

  const [workdayStartTime, setWorkdayStartTime] = useState(() => getUserPreferences().workdayStartTime);
  const [workdayEndTime, setWorkdayEndTime] = useState(() => getUserPreferences().workdayEndTime);
  const [enableMiddayBreak, setEnableMiddayBreak] = useState(() => getUserPreferences().enableMiddayBreak);
  const [returnToBaseForMiddayBreak, setReturnToBaseForMiddayBreak] = useState(() => getUserPreferences().returnToBaseForMiddayBreak);
  const [slotStepMinutes, setSlotStepMinutes] = useState(() => getUserPreferences().slotStepMinutes);
  const [breakStartTime, setBreakStartTime] = useState(() => getUserPreferences().breakStartTime);
  const [breakEndTime, setBreakEndTime] = useState(() => getUserPreferences().breakEndTime);

  const [professionalBase, setProfessionalBase] = useState(() => {
    const preferences = getUserPreferences();
    return getCoordinates(
      preferences.professionalBaseLatitude,
      preferences.professionalBaseLongitude,
    );
  });

  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const response = await clientService.getClients({ page: 1, limit: 1000, active: true });
        if (response.success) {
          setClients(response.data);
        }
      } catch (error) {
        console.error('Erreur de chargement des clients pour suggestions:', error);
        setErrorMessage('Impossible de charger la liste des clients.');
      } finally {
        setIsLoadingClients(false);
      }
    };

    void loadClients();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeUserPreferences((preferences) => {
      setPreferredDuration(preferences.defaultVisitDuration);
      setWorkdayStartTime(preferences.workdayStartTime);
      setWorkdayEndTime(preferences.workdayEndTime);
      setEnableMiddayBreak(preferences.enableMiddayBreak);
      setReturnToBaseForMiddayBreak(preferences.returnToBaseForMiddayBreak);
      setSlotStepMinutes(preferences.slotStepMinutes);
      setBreakStartTime(preferences.breakStartTime);
      setBreakEndTime(preferences.breakEndTime);

      setProfessionalBase(
        getCoordinates(
          preferences.professionalBaseLatitude,
          preferences.professionalBaseLongitude,
        ),
      );
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const selectedClient = useMemo(() => {
    return clients.find((client) => client.id === selectedClientId) || null;
  }, [clients, selectedClientId]);

  const clientHasCoordinates = useMemo(() => {
    if (!selectedClient) {
      return false;
    }
    return getCoordinates(selectedClient.latitude, selectedClient.longitude) !== null;
  }, [selectedClient]);

  const geocodedClients = useMemo(() => {
    return clients.filter((client) => getCoordinates(client.latitude, client.longitude) !== null);
  }, [clients]);

  const handleAnalyze = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setResult(null);

    if (!selectedClientId) {
      setErrorMessage('Selectionnez un client a analyser.');
      return;
    }

    if (earliestDate > latestDate) {
      setErrorMessage('La date de debut doit etre avant la date de fin.');
      return;
    }

    if (!clientHasCoordinates) {
      setErrorMessage('Le client selectionne doit avoir des coordonnees GPS.');
      return;
    }

    setIsAnalyzing(true);
    try {
      const payload = {
        clientId: selectedClientId,
        preferredDuration,
        earliestDate: toIsoDateTime(earliestDate, workdayStartTime),
        latestDate: toIsoDateTime(latestDate, workdayEndTime),
        workdayStartTime,
        workdayEndTime,
        enableMiddayBreak,
        returnToBaseForMiddayBreak,
        breakStartTime: enableMiddayBreak ? breakStartTime : undefined,
        breakEndTime: enableMiddayBreak ? breakEndTime : undefined,
        professionalBase: professionalBase
          ? {
              lat: professionalBase.lat,
              lng: professionalBase.lng,
            }
          : undefined,
        preferredTimeOfDay: preferredTimeOfDay || undefined,
        excludeWeekends,
        includeBuffer,
        maxIdleTimeMinutes,
        slotStepMinutes,
      };

      const suggestionResult = await SuggestionApiService.getOptimalSlot(payload);
      setResult(suggestionResult);
    } catch (error) {
      console.error('Erreur analyse optimisation:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erreur lors de l\'analyse des creneaux.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleScheduleSlot = (slot: SuggestedSlot) => {
    if (!selectedClient?.id) {
      return;
    }

    navigate('/schedule', {
      state: {
        openNewAppointment: true,
        clientId: selectedClient.id,
        clientName: `${selectedClient.firstName} ${selectedClient.lastName}`,
        duration: slot.duration,
        startTime: slot.startTime,
        endTime: slot.endTime,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Suggestions d'optimisation</h1>
        <p className="text-sm text-gray-600">
          Proposez a vos clients les creneaux qui minimisent vos deplacements selon votre agenda existant.
        </p>
      </div>

      <Card title="Generer les meilleurs creneaux" subtitle="Analyse des deplacements avant/apres rendez-vous">
        <form className="space-y-4" onSubmit={handleAnalyze}>
          {!professionalBase && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Base professionnelle non renseignee: les trajets de debut/fin de journee restent estimes.
              Ajoute ta base dans Parametres pour des suggestions plus precises.
            </div>
          )}

          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Horaires appliques: {workdayStartTime} - {workdayEndTime}
            {enableMiddayBreak ? ` · pause ${breakStartTime} - ${breakEndTime}` : ' · pause desactivee'}
            {enableMiddayBreak && (
              <span>
                {' '}· {returnToBaseForMiddayBreak ? 'Retour à la base pour le déjeuner' : 'Pas de retour à la base pour le déjeuner'}
              </span>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="clientId" className="mb-1 block text-sm font-medium text-gray-700">
                Client cible
              </label>
              <select
                id="clientId"
                value={selectedClientId}
                onChange={(event) => setSelectedClientId(event.target.value)}
                disabled={isLoadingClients}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Selectionnez un client</option>
                {geocodedClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.firstName} {client.lastName}
                    {client.city ? ` - ${client.city}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {geocodedClients.length} client(s) geolocalise(s) disponible(s)
              </p>
            </div>

            <div>
              <label htmlFor="preferredDuration" className="mb-1 block text-sm font-medium text-gray-700">
                Duree de visite (minutes)
              </label>
              <input
                id="preferredDuration"
                type="number"
                min={15}
                step={5}
                value={preferredDuration}
                onChange={(event) => setPreferredDuration(Number(event.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="earliestDate" className="mb-1 block text-sm font-medium text-gray-700">
                Debut de recherche
              </label>
              <input
                id="earliestDate"
                type="date"
                value={earliestDate}
                onChange={(event) => setEarliestDate(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="latestDate" className="mb-1 block text-sm font-medium text-gray-700">
                Fin de recherche
              </label>
              <input
                id="latestDate"
                type="date"
                value={latestDate}
                onChange={(event) => setLatestDate(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="preferredTime" className="mb-1 block text-sm font-medium text-gray-700">
                Preference horaire
              </label>
              <select
                id="preferredTime"
                value={preferredTimeOfDay}
                onChange={(event) => setPreferredTimeOfDay(event.target.value as PreferredTimeOfDay | '')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Sans preference</option>
                <option value="morning">Matin (08h-12h)</option>
                <option value="afternoon">Apres-midi (12h-17h)</option>
                <option value="evening">Soiree (17h-20h)</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={excludeWeekends}
                onChange={(event) => setExcludeWeekends(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Exclure les week-ends</span>
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeBuffer}
                onChange={(event) => setIncludeBuffer(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Ajouter un tampon de deplacement</span>
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={returnToBaseForMiddayBreak}
                onChange={(event) => setReturnToBaseForMiddayBreak(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Retour à la base pendant la pause déjeuner</span>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="maxIdleTime" className="mb-1 block text-sm font-medium text-gray-700">
                Attente maxi entre deux rendez-vous
              </label>
              <select
                id="maxIdleTime"
                value={maxIdleTimeMinutes ?? ''}
                onChange={(event) =>
                  setMaxIdleTimeMinutes(
                    event.target.value === '' ? undefined : Number(event.target.value),
                  )
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Sans limite</option>
                <option value="20">20 minutes max</option>
                <option value="30">30 minutes max</option>
                <option value="45">45 minutes max</option>
                <option value="60">1 heure max</option>
                <option value="90">1h30 max</option>
                <option value="120">2 heures max</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Exclut les creneaux avec trop de temps mort entre la fin de la visite et le RDV suivant.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Preference actuelle: {getPreferredTimeLabel(preferredTimeOfDay)}
            </p>
            <Button type="submit" loading={isAnalyzing}>
              <Icon name="lightbulb" className="mr-2" />
              Lancer l'analyse
            </Button>
          </div>
        </form>
      </Card>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {result && (
        <>
          <Card title="Resume optimisation" subtitle="Vision rapide du meilleur scenario" className="space-y-4">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="rounded-lg bg-emerald-50 px-3 py-3">
                <p className="text-xs uppercase text-emerald-700">Confiance</p>
                <p className="text-xl font-semibold text-emerald-900">{Math.round(result.confidence * 100)}%</p>
              </div>
              <div className="rounded-lg bg-blue-50 px-3 py-3">
                <p className="text-xs uppercase text-blue-700">Trajet total</p>
                <p className="text-xl font-semibold text-blue-900">{result.optimizationMetrics.totalTravelTime} min</p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-3">
                <p className="text-xs uppercase text-indigo-700">Distance</p>
                <p className="text-xl font-semibold text-indigo-900">{result.optimizationMetrics.totalDistance} km</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-3">
                <p className="text-xs uppercase text-amber-700">Score</p>
                <p className="text-xl font-semibold text-amber-900">{result.optimizationMetrics.efficiencyScore}/100</p>
              </div>
            </div>

            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{result.explanation}</p>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Creneaux recommandes" subtitle="Meilleurs compromis de deplacement" className="space-y-4">
              <div className="space-y-3">
                {result.suggestedSlots.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucun creneau recommande pour cette periode.</p>
                ) : (
                  result.suggestedSlots.map((slot, index) => (
                    <SlotCard
                      key={`${slot.startTime}-${index}`}
                      slot={slot}
                      index={index}
                      variant="primary"
                      onSchedule={() => handleScheduleSlot(slot)}
                    />
                  ))
                )}
              </div>
            </Card>

            <Card title="Alternatives" subtitle="Options secondaires si le client refuse" className="space-y-4">
              <div className="space-y-3">
                {result.alternativeSlots.length === 0 ? (
                  <p className="text-sm text-gray-500">Pas d'alternative supplementaire sur cette plage.</p>
                ) : (
                  result.alternativeSlots.map((slot, index) => (
                    <SlotCard
                      key={`${slot.startTime}-${index}`}
                      slot={slot}
                      index={index}
                      variant="alternative"
                      onSchedule={() => handleScheduleSlot(slot)}
                    />
                  ))
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default Suggestions;
